import React, { useState, useEffect, useMemo } from 'react';
import {
    Loader2, X, AlertTriangle, CheckCircle, ChevronRight,
    Calendar, Plus, RotateCcw, Droplets, TrendingDown,
    Package, FileText, Info, ChevronDown, ChevronUp, AlertCircle
} from 'lucide-react';
import API_BASE_URL from "../config";
import Swal from 'sweetalert2';

const FUEL_KEYWORDS = ['fuel', 'diesel', 'gasoline', 'petrol', 'kerosene'];
const SHIFTS = ['Morning', 'Afternoon', 'Night'];
const TANK_LOCATIONS = ['Main Tank', 'Reserve Tank', 'Tank 1', 'Tank 2', 'Tank 3'];

const fmt = (d) => {
    if (!d) return '—';
    const date = new Date(d);
    return isNaN(date) ? '—' : date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const fmtNum = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

function FuelGaugeBar({ current, max, label }) {
    const pct = max > 0 ? Math.min(100, (current / max) * 100) : 0;
    const color = pct < 20 ? '#ef4444' : pct < 50 ? '#f59e0b' : '#22c55e';
    const bgColor = pct < 20 ? 'bg-red-500' : pct < 50 ? 'bg-yellow-400' : 'bg-green-500';

    return (
        <div className="w-full">
            <div className="flex justify-between items-center mb-1">
                <span className="text-xs text-gray-500 font-semibold">{label}</span>
                <span className="text-xs font-bold" style={{ color }}>{fmtNum(current)} L</span>
            </div>
            <div className="h-3 bg-blue-100 rounded-full overflow-hidden relative">
                <div className={`h-full rounded-full transition-all duration-700 ${bgColor}`} style={{ width: `${pct}%` }} />
                {[25, 50, 75].map(t => (
                    <div key={t} className="absolute top-0 bottom-0 w-px bg-white/60" style={{ left: `${t}%` }} />
                ))}
            </div>
            <div className="flex justify-between mt-0.5">
                <span className="text-xs text-gray-400">0</span>
                <span className="text-xs font-semibold" style={{ color }}>{pct.toFixed(1)}%</span>
                <span className="text-xs text-gray-400">{fmtNum(max)} L</span>
            </div>
        </div>
    );
}

function ShiftBadge({ shift }) {
    const cfg = {
        Morning: { cls: 'bg-yellow-100 text-yellow-700 border-yellow-200', emoji: '🌅' },
        Afternoon: { cls: 'bg-sky-100 text-sky-700 border-sky-200', emoji: '☀️' },
        Night: { cls: 'bg-indigo-100 text-indigo-700 border-indigo-200', emoji: '🌙' },
    };
    const c = cfg[shift] || { cls: 'bg-gray-100 text-gray-600 border-gray-200', emoji: '⏰' };
    return (
        <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${c.cls}`}>
            {c.emoji} {shift}
        </span>
    );
}

function BatchBadge({ status }) {
    if (status === 'active')
        return <span className="bg-green-100 text-green-700 border border-green-200 text-xs font-bold px-2 py-0.5 rounded-full">● Active</span>;
    if (status === 'closed')
        return <span className="bg-gray-100 text-gray-500 border border-gray-200 text-xs font-bold px-2 py-0.5 rounded-full">✓ Closed</span>;
    return null;
}

function Pagination({ currentPage, totalItems, itemsPerPage, onPageChange }) {
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    if (totalPages <= 1) return null;
    return (
        <div className="flex items-center justify-between px-5 py-3 border-t border-blue-100 bg-white">
            <span className="text-xs text-gray-400 font-semibold">
                Page {currentPage} of {totalPages} · {totalItems} total
            </span>
            <div className="flex items-center gap-1">
                <button
                    onClick={() => onPageChange(1)}
                    disabled={currentPage === 1}
                    className="px-2 py-1 text-xs font-bold text-gray-500 hover:text-blue-700 disabled:opacity-30 disabled:cursor-not-allowed rounded-lg hover:bg-blue-50 transition">
                    «
                </button>
                <button
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-3 py-1 text-xs font-bold text-gray-500 hover:text-blue-700 disabled:opacity-30 disabled:cursor-not-allowed rounded-lg hover:bg-blue-50 transition">
                    ‹ Prev
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                    .reduce((acc, p, idx, arr) => {
                        if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                        acc.push(p);
                        return acc;
                    }, [])
                    .map((p, idx) =>
                        p === '...'
                            ? <span key={`ellipsis-${idx}`} className="px-2 text-xs text-gray-400">...</span>
                            : <button key={p} onClick={() => onPageChange(p)}
                                className={`px-3 py-1 text-xs font-bold rounded-lg transition ${currentPage === p
                                    ? 'bg-blue-600 text-white shadow'
                                    : 'text-gray-500 hover:text-blue-700 hover:bg-blue-50'
                                    }`}>
                                {p}
                            </button>
                    )}
                <button
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 text-xs font-bold text-gray-500 hover:text-blue-700 disabled:opacity-30 disabled:cursor-not-allowed rounded-lg hover:bg-blue-50 transition">
                    Next ›
                </button>
                <button
                    onClick={() => onPageChange(totalPages)}
                    disabled={currentPage === totalPages}
                    className="px-2 py-1 text-xs font-bold text-gray-500 hover:text-blue-700 disabled:opacity-30 disabled:cursor-not-allowed rounded-lg hover:bg-blue-50 transition">
                    »
                </button>
            </div>
        </div>
    );
}
export default function Fuel_diesel() {
    const [activeTab, setActiveTab] = useState('overview');
    const [fuelBatches, setFuelBatches] = useState([]);
    const [fuelLogs, setFuelLogs] = useState([]);
    const [grRecords, setGrRecords] = useState([]);
    const [inventoryData, setInventoryData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showLogForm, setShowLogForm] = useState(false);
    const [showResetModal, setShowResetModal] = useState(false);
    const [showBatchDetail, setShowBatchDetail] = useState(null);
    const [expandedDays, setExpandedDays] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [batchPage, setBatchPage] = useState(1);
    const [dipstickPage, setDipstickPage] = useState(1);
    const ITEMS_PER_PAGE = 5;
    const [invPage, setInvPage] = useState(1);
    const INV_PER_PAGE = 5; // mas marami kasi table siya
    const [logForm, setLogForm] = useState({
        plateNumber: '',
        truckDescription: '',
        dipstickReading: '',
        litersRemaining: '',
        tankLocation: 'Main Tank',
        tankCapacity: '',
        shift: 'Morning',
        logDate: new Date().toISOString().split('T')[0],
        remarks: '',
        fuelBatchId: '',
    });

    const [fuelGROptions, setFuelGROptions] = useState([]);

    const [resetForm, setResetForm] = useState({
        newDeliveryLiters: '',
        grReference: '',
        notes: '',
        deliveryDate: new Date().toISOString().split('T')[0],
    });

    const fetchAll = async () => {
        setLoading(true);
        try {
            const [batchRes, logRes, invRes, grRes] = await Promise.all([
                fetch(`${API_BASE_URL}api/FuelBatch`).catch(() => null),
                fetch(`${API_BASE_URL}api/FuelLog`).catch(() => null),
                fetch(`${API_BASE_URL}api/InventoryData`).catch(() => null),
                fetch(`${API_BASE_URL}api/GR_Header`).catch(() => null),
            ]);

            if (batchRes?.ok) {
                const raw = await batchRes.json().catch(() => []);
                const sorted = [...raw].sort((a, b) => b.id - a.id);
                setFuelBatches(sorted.map((b, i) => {
                    // Parse "Remaining Fuel Liters Batch X: 1200L" from remarks
                    const remainingMatch = b.remarks?.match(/Remaining Fuel Liters[^:]+:\s*([\d.]+)L/i);
                    const oldRemainingLiters = remainingMatch ? Number(remainingMatch[1]) : 0;

                    // Parse GR Ref from remarks
                    const grRefMatch = b.remarks?.match(/GR Ref:\s*([^\s|]+)/i);
                    const grReference = grRefMatch ? grRefMatch[1] : null;

                    return {
                        ...b,
                        batchNo: sorted.length - i,
                        batchLabel: `Batch ${sorted.length - i}`,
                        status: i === 0 ? 'active' : 'closed',
                        totalStartLiters: Number(b.liters || 0),
                        newDeliveryLiters: Number(b.liters || 0),
                        oldRemainingLiters,   // ✅ parsed from remarks
                        grReference,          // ✅ parsed from remarks
                        deliveryDate: b.batchDate,
                        createdByName: b.createdByName,
                    };
                }));
            } if (logRes?.ok) setFuelLogs(await logRes.json().catch(() => []));

            if (invRes?.ok) {
                const inv = await invRes.json().catch(() => []);
                const fuel = inv.filter(i =>
                    i.itemCode?.toUpperCase() === 'GS1438' ||
                    i.itemDescription?.toUpperCase().includes('FUEL DIESEL')
                );
                setInventoryData(fuel);
            }

            if (grRes?.ok) {
                const grs = await grRes.json().catch(() => []);

                // ✅ Original: filter fuel GRs for grRecords state
                const fuelGRs = grs.filter(gr =>
                    FUEL_KEYWORDS.some(kw => gr.remarks?.toLowerCase().includes(kw)) ||
                    gr.isFuel === true
                );
                setGrRecords(fuelGRs);

                // ✅ NEW: Build fuelGROptions — GRs that contain GS1438 items
                const fuelGRList = [];
                for (const gr of grs) {
                    // grDetails may already be included in GR_Header response
                    const details = gr.grDetails || [];
                    const gs1438Items = details.filter(
                        d => d.itemCode?.toUpperCase() === 'GS1438'
                    );

                    if (gs1438Items.length > 0) {
                        const totalLiters = gs1438Items.reduce(
                            (s, d) => s + (Number(d.receivedQty) || 0), 0
                        );
                        fuelGRList.push({
                            grNo: gr.grNo,
                            grDate: gr.grDate,
                            liters: totalLiters,
                        });
                    }
                }

                // ✅ If grDetails not embedded, fallback: fetch each GR individually
                // Only do this if fuelGRList is empty but we have GRs
                if (fuelGRList.length === 0 && grs.length > 0) {
                    const detailFetches = grs.map(gr =>
                        fetch(`${API_BASE_URL}api/GR_Header/${gr.id}`)
                            .then(r => r.ok ? r.json() : null)
                            .catch(() => null)
                    );
                    const detailed = await Promise.all(detailFetches);
                    for (const gr of detailed) {
                        if (!gr) continue;
                        const gs1438Items = (gr.grDetails || []).filter(
                            d => d.itemCode?.toUpperCase() === 'GS1438'
                        );
                        if (gs1438Items.length > 0) {
                            const totalLiters = gs1438Items.reduce(
                                (s, d) => s + (Number(d.receivedQty) || 0), 0
                            );
                            fuelGRList.push({
                                grNo: gr.grNo,
                                grDate: gr.grDate,
                                liters: totalLiters,
                            });
                        }
                    }
                }

                setFuelGROptions(fuelGRList);
                console.log(`✅ Fuel GR options loaded: ${fuelGRList.length} GRs with GS1438`);
            }

        } catch (err) {
            console.error('Fetch error:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchAll(); }, []);

    const activeBatch = useMemo(() =>
        fuelBatches.find(b => b.status === 'active') || fuelBatches[0] || null,
        [fuelBatches]
    );

    const totalInventoryLiters = useMemo(() =>
        inventoryData.reduce((s, i) => s + (Number(i.quantity) || 0), 0),
        [inventoryData]
    );

    const activeBatchLogs = useMemo(() =>
        activeBatch ? fuelLogs.filter(l => l.fuelBatchId === activeBatch.id) : fuelLogs,
        [fuelLogs, activeBatch]
    );

    const latestLog = useMemo(() =>
        activeBatchLogs.length > 0
            ? [...activeBatchLogs].sort((a, b) => new Date(b.logDate || b.dateCreated) - new Date(a.logDate || a.dateCreated))[0]
            : null,
        [activeBatchLogs]
    );

    const weeklyConsumed = useMemo(() => {
        const weekAgo = new Date(); weekAgo.setDate(weekAgo.getDate() - 6);
        return activeBatchLogs
            .filter(l => new Date(l.logDate || l.dateCreated) >= weekAgo)
            .reduce((s, l) => s + (Number(l.litersConsumed) || 0), 0);
    }, [activeBatchLogs]);

    const groupedByDay = useMemo(() => {
        const map = {};
        activeBatchLogs.forEach(log => {
            const day = (log.logDate || log.dateCreated || '').split('T')[0];
            if (!map[day]) map[day] = [];
            map[day].push(log);
        });
        return Object.entries(map)
            .sort(([a], [b]) => b.localeCompare(a))
            .map(([date, logs]) => ({
                date,
                logs: [...logs].sort((a, b) => new Date(b.dateCreated || b.logDate) - new Date(a.dateCreated || a.logDate)),
                minRemaining: Math.min(...logs.map(l => Number(l.litersRemaining) || 0)),
                totalConsumed: logs.reduce((s, l) => s + (Number(l.litersConsumed) || 0), 0),
            }));
    }, [activeBatchLogs]);

    const handleSaveLog = async () => {
        if (!logForm.litersRemaining) {
            Swal.fire({ icon: 'warning', title: 'Required', text: 'Liters remaining is required.', confirmButtonColor: '#2563eb' });
            return;
        }
        setSubmitting(true);
        try {
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            const payload = {
                plateNumber: logForm.plateNumber,
                truckDescription: logForm.truckDescription,
                dipstickReading: parseFloat(logForm.dipstickReading) || 0,
                litersRemaining: parseFloat(logForm.litersRemaining) || 0,
                tankLocation: logForm.tankLocation,
                tankCapacity: logForm.tankCapacity ? parseFloat(logForm.tankCapacity) : null,
                shift: logForm.shift,
                logDate: new Date(logForm.logDate).toISOString(),
                remarks: logForm.remarks,
                fuelBatchId: activeBatch?.id || null,
                loggedBy: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username || 'System',
                loggedByUserId: user.id || user.userId,
            };
            const res = await fetch(`${API_BASE_URL}api/FuelLog`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!res.ok) throw new Error('Failed to save');
            await Swal.fire({ icon: 'success', title: 'Log Saved!', timer: 1500, showConfirmButton: false });
            setShowLogForm(false);
            setLogForm(prev => ({ ...prev, dipstickReading: '', litersRemaining: '', remarks: '', plateNumber: '', truckDescription: '' }));
            fetchAll();
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Error', text: err.message, confirmButtonColor: '#ef4444' });
        } finally {
            setSubmitting(false);
        }
    };



    const handleReset = async () => {
        if (!resetForm.newDeliveryLiters) {
            Swal.fire({ icon: 'warning', title: 'Required', text: 'New delivery liters is required.', confirmButtonColor: '#2563eb' });
            return;
        }

        const sortedInv = [...inventoryData].sort((a, b) => b.id - a.id);
        const latestInvRow = sortedInv[0];
        const oldBatchRows = sortedInv.slice(1);

        const activeBatchAllLogs = fuelLogs
            .filter(l => l.fuelBatchId === activeBatch?.id)
            .sort((a, b) => new Date(b.logDate || b.dateCreated) - new Date(a.logDate || a.dateCreated));

        const latestActiveBatchLog = activeBatchAllLogs[0] || null;

        const oldRemaining = latestActiveBatchLog?.litersRemaining != null
            ? Number(latestActiveBatchLog.litersRemaining)
            : Number(activeBatch?.totalStartLiters || 0);

        // ✅ FIX: oldBatchLabel = current active batch (e.g. "Batch 3")
        const oldBatchLabel = activeBatch?.batchLabel || 'Latest Batch';

        const newLiters = Number(resetForm.newDeliveryLiters);

        // ✅ FIX: currentBatchNo = active batch number, nextBatchNo = +1
        const currentBatchNo = activeBatch?.batchNo || 1;
        const nextBatchNo = currentBatchNo + 1;

        const confirm = await Swal.fire({
            icon: 'question',
            title: 'Start New Batch?',
            html: `<div style="text-align:left;font-size:14px;line-height:1.6">
            <b>Inventory breakdown:</b><br/>
            ${oldBatchRows.map(r =>
                `<span style="color:#6b7280">├─ ${r.batchNo || 'Batch'}: <b>${fmtNum(r.quantity)} L</b> ← will be 0</span><br/>`
            ).join('')}
            <span style="color:#ef4444">└─ ${oldBatchLabel}: <b>${fmtNum(oldRemaining)} L</b> → replaced by new GR</span><br/>
            <br/>
            <span style="color:#2563eb"><b>New Batch ${nextBatchNo}: ${fmtNum(newLiters)} L</b> (new GR only)</span><br/>
            <span style="color:#6b7280;font-size:12px">Inventory after reset: ${fmtNum(newLiters)} L total</span>
        </div>`,
            showCancelButton: true,
            confirmButtonColor: '#2563eb',
            cancelButtonColor: '#6b7280',
            confirmButtonText: `Yes, Start Batch ${nextBatchNo}`,
        });
        if (!confirm.isConfirmed) return;

        setSubmitting(true);
        try {
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            const userId = user?.id || user?.userId || 0;
            const userName = `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username || 'System';

            // Step 1: Close current FuelBatch
            if (activeBatch) {
                await fetch(`${API_BASE_URL}api/FuelBatch/${activeBatch.id}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        ...activeBatch,
                        status: 'closed',
                        closedAt: new Date().toISOString(),
                        closingLiters: oldRemaining,
                        remarks: [
                            `Batch ${nextBatchNo} Reset`,
                            `Remaining Fuel Liters ${oldBatchLabel}: ${Number(oldRemaining)}L`,
                            `New GR delivery: ${newLiters}L`,
                            resetForm.grReference ? `GR Ref: ${resetForm.grReference}` : '',
                            resetForm.notes || '',
                        ].filter(Boolean).join(' | '),
                    }),
                }).catch(e => console.error('Close batch error:', e));
            }

            // Step 2: Create new FuelBatch
            const newBatchPayload = {
                liters: newLiters,
                batchDate: new Date(resetForm.deliveryDate).toISOString(),
                remarks: [
                    `Batch ${nextBatchNo} Reset`,
                    `Remaining Fuel Liters ${oldBatchLabel}: ${Number(oldRemaining)}L`,
                    `New GR delivery: ${newLiters}L`,
                    resetForm.grReference ? `GR Ref: ${resetForm.grReference}` : '',
                    resetForm.notes || '',
                ].filter(Boolean).join(' | '),
                createdBy: userId,
                createdByName: userName,
            };

            const newBatchRes = await fetch(`${API_BASE_URL}api/FuelBatch`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(newBatchPayload),
            });
            if (!newBatchRes.ok) throw new Error('Failed to create new batch');

            // Step 3: zero out older batches, replace latest
            await fetch(`${API_BASE_URL}api/InventoryData/reset-fuel`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    newLiters: newLiters,
                    grReference: resetForm.grReference || null,
                    resetBy: userName,
                }),
            });

            // Step 4: Audit log
            await fetch(`${API_BASE_URL}api/Logs`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'RESET',
                    module: 'Fuel Monitor',
                    description: `Batch ${nextBatchNo} started. ${oldBatchLabel} remaining (${Number(oldRemaining)}L) replaced by new GR (${newLiters}L). GR: ${resetForm.grReference || 'N/A'}`,
                    userId: user?.userId,
                    username: user?.username,
                    fullName: user?.fullName || user?.username,
                }),
            }).catch(() => { });

            await Swal.fire({
                icon: 'success',
                title: `Batch ${nextBatchNo} Started!`,
                html: `${oldBatchLabel} remaining: <b>${fmtNum(oldRemaining)} L</b><br/>
                   New GR delivery: <b style="color:#2563eb">${fmtNum(newLiters)} L</b><br/>
                   <small style="color:#6b7280">Older batches zeroed out ✅</small>`,
                timer: 2500,
                showConfirmButton: false,
            });

            setShowResetModal(false);
            setResetForm({ newDeliveryLiters: '', grReference: '', notes: '', deliveryDate: new Date().toISOString().split('T')[0] });
            fetchAll();

        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Error', text: err.message, confirmButtonColor: '#ef4444' });
        } finally {
            setSubmitting(false);
        }
    };
    const handleDeleteLog = async (id) => {
        const res = await Swal.fire({
            title: 'Delete this entry?', icon: 'warning',
            showCancelButton: true, confirmButtonColor: '#ef4444',
            confirmButtonText: 'Delete',
        });
        if (!res.isConfirmed) return;
        await fetch(`${API_BASE_URL}api/FuelLog/${id}`, { method: 'DELETE' });
        fetchAll();
    };

    const toggleDay = (date) => setExpandedDays(p => ({ ...p, [date]: !p[date] }));

    if (loading) return (
        <div className="min-h-screen bg-blue-50 flex items-center justify-center">
            <div className="text-center">
                <Loader2 className="w-10 h-10 text-blue-600 animate-spin mx-auto mb-3" />
                <p className="text-gray-500 text-sm font-semibold">Loading Fuel Monitor...</p>
            </div>
        </div>
    );

    const currentLiters = totalInventoryLiters || (latestLog?.litersRemaining ?? 0);
    const maxLiters = totalInventoryLiters || activeBatch?.totalStartLiters || 1;
    return (
        <div className="min-h-screen bg-blue-50 text-gray-800 p-4 md:p-6 font-sans">
            <div className="max-w-7xl mx-auto">

                {/* PAGE HEADER */}
                <div className="mb-6">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                            <div className="w-14 h-14 bg-blue-100 border border-blue-200 rounded-2xl flex items-center justify-center">
                                <span className="text-3xl">⛽</span>
                            </div>
                            <div>
                                <h1 className="text-2xl font-black text-blue-900 tracking-tight">Fuel Monitor</h1>
                                <p className="text-gray-500 text-sm mt-0.5">Diesel inventory · Batch tracking · Dipstick logs</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                            <button onClick={fetchAll}
                                className="px-3 py-2 rounded-xl border border-blue-200 hover:border-blue-400 text-gray-500 hover:text-blue-700 text-sm font-semibold transition-all bg-white">
                                ↻ Refresh
                            </button>
                            <button onClick={() => setShowLogForm(v => !v)}
                                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl text-sm transition-all shadow-lg shadow-blue-200">
                                <Plus className="w-4 h-4" /> Log Dipstick
                            </button>
                            <button onClick={() => setShowResetModal(true)}
                                className="flex items-center gap-2 px-4 py-2 bg-blue-800 hover:bg-blue-900 text-white font-bold rounded-xl text-sm transition-all shadow-lg shadow-blue-200">
                                <RotateCcw className="w-4 h-4" /> New GR / Reset Batch
                            </button>
                        </div>
                    </div>
                </div>

                {/* ACTIVE BATCH BANNER */}
                {activeBatch && (
                    <div className="bg-white border border-blue-200 rounded-2xl p-4 mb-5 flex items-center gap-4 flex-wrap shadow-sm">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                                <Package className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                                <p className="text-xs text-blue-500 font-bold uppercase tracking-wider">Active Batch</p>
                                <p className="text-blue-900 font-black text-lg">{activeBatch.batchLabel}</p>
                            </div>
                        </div>
                        <div className="h-px bg-blue-100 flex-1 hidden sm:block" />
                        <div className="flex flex-wrap gap-6 text-sm">
                            <div>
                                <p className="text-gray-400 text-xs">Started</p>
                                <p className="text-gray-700 font-semibold">{fmt(activeBatch.deliveryDate)}</p>
                            </div>
                            <div>
                                <p className="text-gray-400 text-xs">Delivery Volume</p>
                                <p className="text-blue-600 font-bold">{fmtNum(activeBatch.newDeliveryLiters)} L</p>
                            </div>
                            <div>
                                <p className="text-gray-400 text-xs">Opening Stock</p>
                                <p className="text-blue-800 font-bold">{fmtNum(activeBatch.totalStartLiters)} L</p>
                            </div>
                            {activeBatch.grReference && (
                                <div>
                                    <p className="text-gray-400 text-xs">GR Ref</p>
                                    <p className="text-gray-700 font-mono font-semibold">{activeBatch.grReference}</p>
                                </div>
                            )}
                        </div>
                        <button onClick={() => setShowBatchDetail(activeBatch)}
                            className="ml-auto text-xs text-blue-600 hover:text-blue-800 font-semibold border border-blue-200 hover:border-blue-400 px-3 py-1.5 rounded-lg transition-all bg-blue-50">
                            View Details
                        </button>
                    </div>
                )}

                {/* LOG FORM */}
                {showLogForm && (
                    <div className="bg-white border border-blue-200 rounded-2xl p-5 mb-5 shadow-lg">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                                <Droplets className="w-5 h-5 text-blue-500" />
                                <h3 className="text-blue-900 font-bold">New Dipstick Log Entry</h3>
                            </div>
                            <button onClick={() => setShowLogForm(false)} className="text-gray-400 hover:text-gray-600 transition">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            {[
                                { label: 'Log Date', key: 'logDate', type: 'date' },
                                { label: 'Shift', key: 'shift', type: 'select', options: SHIFTS },
                                { label: 'Tank Location', key: 'tankLocation', type: 'select', options: TANK_LOCATIONS },
                                { label: 'Tank Capacity (L)', key: 'tankCapacity', type: 'number', placeholder: 'e.g. 20000' },
                                { label: 'Plate Number', key: 'plateNumber', type: 'text', placeholder: 'e.g. ABC-1234' },
                                { label: 'Truck Description', key: 'truckDescription', type: 'text', placeholder: 'e.g. Dump truck' },
                                { label: 'Dipstick Reading (cm)', key: 'dipstickReading', type: 'number', placeholder: 'e.g. 85.5' },
                                { label: 'Liters Remaining *', key: 'litersRemaining', type: 'number', placeholder: 'e.g. 15000', highlight: true },
                            ].map(field => (
                                <div key={field.key}>
                                    <label className={`block text-xs font-bold mb-1.5 uppercase tracking-wide ${field.highlight ? 'text-blue-600' : 'text-gray-500'}`}>
                                        {field.label}
                                    </label>
                                    {field.type === 'select' ? (
                                        <select value={logForm[field.key]}
                                            onChange={e => setLogForm(p => ({ ...p, [field.key]: e.target.value }))}
                                            className="w-full bg-blue-50 border border-blue-200 rounded-xl px-3 py-2.5 text-sm text-gray-700 focus:outline-none focus:border-blue-500 transition">
                                            {field.options.map(o => <option key={o}>{o}</option>)}
                                        </select>
                                    ) : (
                                        <input type={field.type} value={logForm[field.key]} placeholder={field.placeholder}
                                            onChange={e => setLogForm(p => ({ ...p, [field.key]: e.target.value }))}
                                            className={`w-full bg-blue-50 border rounded-xl px-3 py-2.5 text-sm text-gray-700 focus:outline-none transition
                                                    ${field.highlight ? 'border-blue-400 focus:border-blue-600 font-bold' : 'border-blue-200 focus:border-blue-500'}`} />
                                    )}
                                </div>
                            ))}
                            <div className="sm:col-span-2 lg:col-span-4">
                                <label className="block text-xs font-bold mb-1.5 uppercase tracking-wide text-gray-500">Remarks</label>
                                <input type="text" value={logForm.remarks} placeholder="Optional notes..."
                                    onChange={e => setLogForm(p => ({ ...p, remarks: e.target.value }))}
                                    className="w-full bg-blue-50 border border-blue-200 rounded-xl px-3 py-2.5 text-sm text-gray-700 focus:outline-none focus:border-blue-500 transition" />
                            </div>
                        </div>
                        <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-blue-100">
                            <button onClick={() => setShowLogForm(false)}
                                className="px-5 py-2.5 rounded-xl border border-blue-200 text-gray-500 hover:text-gray-700 text-sm font-semibold transition">
                                Cancel
                            </button>
                            <button onClick={handleSaveLog} disabled={submitting}
                                className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white font-bold rounded-xl text-sm transition shadow-lg">
                                {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</> : <><CheckCircle className="w-4 h-4" /> Save Log</>}
                            </button>
                        </div>
                    </div>
                )}

                {/* TABS */}
                <div className="flex gap-1 bg-white border border-blue-100 rounded-xl p-1 mb-5 w-fit shadow-sm">
                    {[
                        { key: 'overview', label: 'Overview', emoji: '📊' },
                        { key: 'dipstick', label: 'Dipstick Logs', emoji: '📋' },
                        { key: 'batches', label: 'Batch History', emoji: '📦' },
                    ].map(t => (
                        <button key={t.key} onClick={() => setActiveTab(t.key)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all whitespace-nowrap
                                    ${activeTab === t.key ? 'bg-blue-600 text-white shadow-md' : 'text-gray-500 hover:text-blue-700 hover:bg-blue-50'}`}>
                            {t.emoji} {t.label}
                        </button>
                    ))}
                </div>

                {activeTab === 'overview' && (
                    <div className="space-y-5">
                        {/* KPI Cards */}
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                            {[
                                { label: 'Current Level', value: `${fmtNum(currentLiters)} L`, sub: 'Latest dipstick reading', color: 'text-blue-700', bg: 'bg-blue-50', border: 'border-blue-200', emoji: '⛽' },
                                { label: 'Batch Opening', value: `${fmtNum(activeBatch?.totalStartLiters || 0)} L`, sub: 'Old remaining + New delivery', color: 'text-blue-500', bg: 'bg-blue-50', border: 'border-blue-200', emoji: '📥' },
                                { label: 'Consumed (7d)', value: `${fmtNum(weeklyConsumed)} L`, sub: 'Last 7 days dipstick', color: 'text-red-500', bg: 'bg-red-50', border: 'border-red-200', emoji: '📉' },
                                { label: 'Log Entries', value: activeBatchLogs.length, sub: 'Current batch total', color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200', emoji: '📋' },
                            ].map((card, i) => (
                                <div key={i} className={`${card.bg} border ${card.border} rounded-2xl p-4 shadow-sm`}>
                                    <div className="flex items-center justify-between mb-3">
                                        <span className="text-xs text-gray-500 font-bold uppercase tracking-wide">{card.label}</span>
                                        <span className="text-2xl">{card.emoji}</span>
                                    </div>
                                    <p className={`text-2xl font-black ${card.color}`}>{card.value}</p>
                                    <p className="text-xs text-gray-400 mt-1">{card.sub}</p>
                                </div>
                            ))}
                        </div>

                        {/* Fuel Level Gauge */}
                        <div className="bg-white border border-blue-100 rounded-2xl p-5 shadow-sm">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-blue-900 font-bold flex items-center gap-2">
                                    <TrendingDown className="w-5 h-5 text-blue-500" />
                                    Current Fuel Level
                                </h3>
                                {activeBatch && <span className="text-xs text-gray-400">{activeBatch.batchLabel}</span>}
                            </div>
                            <FuelGaugeBar current={Number(currentLiters)} max={Number(maxLiters)} label="Remaining vs Opening Stock" />
                            {activeBatch && (
                                <div className="mt-4 grid grid-cols-3 gap-3 pt-4 border-t border-blue-100">
                                    <div className="text-center">
                                        <p className="text-xs text-gray-400 mb-1">Old Remaining</p>
                                        <p className="text-blue-500 font-bold text-sm">{fmtNum(activeBatch.oldRemainingLiters)} L</p>
                                    </div>
                                    <div className="text-center">
                                        <p className="text-xs text-gray-400 mb-1">+ New Delivery</p>
                                        <p className="text-green-500 font-bold text-sm">{fmtNum(activeBatch.newDeliveryLiters)} L</p>
                                    </div>
                                    <div className="text-center">
                                        <p className="text-xs text-gray-400 mb-1">= Total Opening</p>
                                        <p className="text-blue-700 font-black text-base">{fmtNum(activeBatch.totalStartLiters)} L</p>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Inventory from GR */}
                        {inventoryData.length > 0 && (
                            <div className="bg-white border border-blue-100 rounded-2xl overflow-hidden shadow-sm">
                                <div className="px-5 py-3.5 border-b border-blue-100 flex items-center gap-2 bg-blue-50">
                                    <Package className="w-4 h-4 text-blue-600" />
                                    <h3 className="text-blue-900 font-bold text-sm">Fuel Inventory (from GR)</h3>
                                    <span className="ml-auto text-xs text-gray-400">
                                        {inventoryData.length} item{inventoryData.length !== 1 ? 's' : ''}
                                    </span>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="border-b border-blue-100 bg-blue-50/50">
                                                {['Item Code', 'Description', 'Warehouse', 'UOM', 'Total Qty', 'Available', 'Reserved'].map(h => (
                                                    <th key={h} className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {inventoryData
                                                .slice((invPage - 1) * INV_PER_PAGE, invPage * INV_PER_PAGE)
                                                .map((item, i) => (
                                                    <tr key={i} className="border-b border-blue-50 hover:bg-blue-50/40 transition-colors">
                                                        <td className="px-4 py-3">
                                                            <span className="font-mono font-bold text-blue-700 bg-blue-100 px-2 py-0.5 rounded text-xs">{item.itemCode}</span>
                                                        </td>
                                                        <td className="px-4 py-3 text-gray-700 font-medium">{item.itemDescription}</td>
                                                        <td className="px-4 py-3">
                                                            <span className="bg-blue-50 text-gray-600 px-2 py-0.5 rounded text-xs font-semibold border border-blue-200">{item.warehouseCode}</span>
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <span className="bg-blue-100 text-blue-700 border border-blue-200 px-2 py-0.5 rounded text-xs font-bold font-mono">{item.uom}</span>
                                                        </td>
                                                        <td className="px-4 py-3 font-bold text-gray-800">{fmtNum(item.quantity)}</td>
                                                        <td className="px-4 py-3 font-bold text-green-600">{fmtNum(item.availableQty)}</td>
                                                        <td className="px-4 py-3 font-bold text-blue-500">{fmtNum(item.reservedQty)}</td>
                                                    </tr>
                                                ))}
                                        </tbody>
                                    </table>
                                </div>
                                {/* ✅ PAGINATION - Inventory */}
                                <Pagination
                                    currentPage={invPage}
                                    totalItems={inventoryData.length}
                                    itemsPerPage={INV_PER_PAGE}
                                    onPageChange={(p) => setInvPage(p)}
                                />
                            </div>
                        )}

                        {/* No batch yet */}
                        {!activeBatch && (
                            <div className="bg-white border border-blue-200 rounded-2xl p-10 text-center shadow-sm">
                                <span className="text-5xl mb-4 block">⛽</span>
                                <h3 className="text-blue-900 font-bold text-lg mb-2">No Active Batch</h3>
                                <p className="text-gray-500 text-sm mb-4">Click "New GR / Reset Batch" to start your first batch.</p>
                                <button onClick={() => setShowResetModal(true)}
                                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl text-sm transition-all">
                                    <RotateCcw className="w-4 h-4" /> Start First Batch
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {/* TAB: DIPSTICK LOGS */}
                {activeTab === 'dipstick' && (
                    <div className="bg-white border border-blue-100 rounded-2xl overflow-hidden shadow-sm">
                        <div className="px-5 py-4 border-b border-blue-100 flex items-center justify-between bg-blue-50">
                            <div className="flex items-center gap-3">
                                <Droplets className="w-5 h-5 text-blue-500" />
                                <h3 className="text-blue-900 font-bold">Dipstick Logs</h3>
                                <span className="bg-blue-100 text-blue-700 border border-blue-200 text-xs font-bold px-2 py-0.5 rounded-full">
                                    {activeBatchLogs.length} entries
                                </span>
                                {activeBatch && <span className="text-xs text-gray-400">· {activeBatch.batchLabel}</span>}
                            </div>
                            <button onClick={() => setShowLogForm(v => !v)}
                                className="flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition">
                                <Plus className="w-3.5 h-3.5" /> Add Entry
                            </button>
                        </div>

                        {groupedByDay.length === 0 ? (
                            <div className="text-center py-16">
                                <Droplets className="w-12 h-12 mx-auto mb-3 text-blue-200" />
                                <p className="font-semibold text-gray-500">No dipstick logs yet</p>
                                <p className="text-xs mt-1 text-gray-400">Click "Add Entry" to record your first dipstick reading</p>
                            </div>
                        ) : (
                            <>
                                <div className="divide-y divide-blue-50">
                                    {groupedByDay
                                        .slice((dipstickPage - 1) * ITEMS_PER_PAGE, dipstickPage * ITEMS_PER_PAGE)
                                        .map(({ date, logs, minRemaining, totalConsumed }) => {
                                            const isExpanded = expandedDays[date];
                                            const isToday = date === new Date().toISOString().split('T')[0];
                                            const dateLabel = new Date(date + 'T00:00:00').toLocaleDateString('en-US', {
                                                weekday: 'short', month: 'short', day: 'numeric', year: 'numeric'
                                            });
                                            return (
                                                <div key={date}>
                                                    <button onClick={() => toggleDay(date)}
                                                        className="w-full flex items-center gap-4 px-5 py-3.5 hover:bg-blue-50 transition text-left">
                                                        <ChevronRight className={`w-4 h-4 text-blue-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                                                        <div className="flex items-center gap-2 min-w-[180px]">
                                                            <Calendar className="w-4 h-4 text-blue-500" />
                                                            <span className="text-sm font-bold text-gray-700">{dateLabel}</span>
                                                            {isToday && <span className="bg-blue-600 text-white text-xs font-black px-2 py-0.5 rounded-full">TODAY</span>}
                                                        </div>
                                                        <div className="flex items-center gap-8 ml-auto">
                                                            <div className="text-center">
                                                                <p className="text-xs text-gray-400">Entries</p>
                                                                <p className="text-sm font-bold text-gray-600">{logs.length}</p>
                                                            </div>
                                                            <div className="text-center">
                                                                <p className="text-xs text-gray-400">Consumed</p>
                                                                <p className="text-sm font-bold text-blue-600">{fmtNum(totalConsumed)} L</p>
                                                            </div>
                                                            <div className="text-center">
                                                                <p className="text-xs text-gray-400">Min Remaining</p>
                                                                <p className={`text-sm font-bold ${minRemaining < 5000 ? 'text-red-500' : 'text-green-600'}`}>
                                                                    {fmtNum(minRemaining)} L
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </button>
                                                    {isExpanded && (
                                                        <div className="border-t border-blue-100 bg-blue-50/30">
                                                            <div className="overflow-x-auto">
                                                                <table className="w-full text-sm">
                                                                    <thead>
                                                                        <tr className="border-b border-blue-100">
                                                                            {['#', 'Shift', 'Tank', 'Plate', 'Dipstick', 'Liters Remaining', 'Consumed', 'Logged By', 'Remarks', ''].map(h => (
                                                                                <th key={h} className="px-4 py-2.5 text-left text-xs font-bold text-gray-400 uppercase tracking-wide whitespace-nowrap">{h}</th>
                                                                            ))}
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {logs.map((log, idx) => (
                                                                            <tr key={log.id} className="border-b border-blue-50 hover:bg-blue-50 transition-colors">
                                                                                <td className="px-4 py-3 text-gray-400 text-xs">{idx + 1}</td>
                                                                                <td className="px-4 py-3"><ShiftBadge shift={log.shift} /></td>
                                                                                <td className="px-4 py-3">
                                                                                    <span className="bg-blue-50 text-gray-500 px-2 py-0.5 rounded text-xs border border-blue-200">{log.tankLocation || '—'}</span>
                                                                                </td>
                                                                                <td className="px-4 py-3 text-gray-500 text-xs font-mono">{log.plateNumber || '—'}</td>
                                                                                <td className="px-4 py-3">
                                                                                    <span className="text-gray-700 font-bold">{log.dipstickReading ?? '—'}</span>
                                                                                    <span className="text-gray-400 text-xs ml-1">cm</span>
                                                                                </td>
                                                                                <td className="px-4 py-3">
                                                                                    <span className={`font-black text-base ${Number(log.litersRemaining) < 5000 ? 'text-red-500' : 'text-green-600'}`}>
                                                                                        {fmtNum(log.litersRemaining)}
                                                                                    </span>
                                                                                    <span className="text-gray-400 text-xs ml-1">L</span>
                                                                                    {log.tankCapacity && (
                                                                                        <div className="mt-1 w-20 h-1.5 bg-blue-100 rounded-full overflow-hidden">
                                                                                            <div className={`h-full rounded-full ${Number(log.litersRemaining) / Number(log.tankCapacity) < 0.2 ? 'bg-red-500' : 'bg-blue-500'}`}
                                                                                                style={{ width: `${Math.min(100, (Number(log.litersRemaining) / Number(log.tankCapacity)) * 100)}%` }} />
                                                                                        </div>
                                                                                    )}
                                                                                </td>
                                                                                <td className="px-4 py-3">
                                                                                    {Number(log.litersConsumed) > 0
                                                                                        ? <span className="text-blue-600 font-bold">-{fmtNum(log.litersConsumed)} L</span>
                                                                                        : <span className="text-gray-300 text-xs">—</span>}
                                                                                </td>
                                                                                <td className="px-4 py-3 text-xs text-gray-500">{log.loggedBy || '—'}</td>
                                                                                <td className="px-4 py-3 text-xs text-gray-400 max-w-[120px] truncate">{log.remarks || '—'}</td>
                                                                                <td className="px-4 py-3">
                                                                                    <button onClick={() => handleDeleteLog(log.id)}
                                                                                        className="text-gray-300 hover:text-red-500 transition text-xs" title="Delete">✕</button>
                                                                                </td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                    <tfoot>
                                                                        <tr className="border-t border-blue-200 bg-blue-50">
                                                                            <td colSpan={4} className="px-4 py-2.5 text-xs font-bold text-blue-700 uppercase">Day Total</td>
                                                                            <td />
                                                                            <td className="px-4 py-2.5 text-xs font-bold text-green-600">Min: {fmtNum(minRemaining)} L</td>
                                                                            <td className="px-4 py-2.5 text-xs font-bold text-blue-600">{fmtNum(totalConsumed)} L consumed</td>
                                                                            <td colSpan={3} />
                                                                        </tr>
                                                                    </tfoot>
                                                                </table>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                </div>
                                {/* ✅ PAGINATION - Dipstick */}
                                <Pagination
                                    currentPage={dipstickPage}
                                    totalItems={groupedByDay.length}
                                    itemsPerPage={ITEMS_PER_PAGE}
                                    onPageChange={(p) => {
                                        setDipstickPage(p);
                                        setExpandedDays({});
                                    }}
                                />
                            </>
                        )}
                    </div>
                )}



                {activeTab === 'batches' && (
                    <div className="space-y-4">
                        {fuelBatches.length === 0 ? (
                            <div className="bg-white border border-blue-100 rounded-2xl p-10 text-center shadow-sm">
                                <Package className="w-12 h-12 mx-auto mb-3 text-blue-200" />
                                <p className="text-gray-500 font-semibold">No batches recorded yet</p>
                                <p className="text-xs text-gray-400 mt-1">Click "New GR / Reset Batch" to start</p>
                            </div>
                        ) : (
                            <>
                                {[...fuelBatches]
                                    .sort((a, b) => b.batchNo - a.batchNo)
                                    .slice((batchPage - 1) * ITEMS_PER_PAGE, batchPage * ITEMS_PER_PAGE)
                                    .map((batch) => {
                                        const batchLogs = fuelLogs.filter(l => l.fuelBatchId === batch.id);
                                        const lastLog = [...batchLogs].sort((a, b) => new Date(b.logDate || b.dateCreated) - new Date(a.logDate || a.dateCreated))[0];
                                        const consumed = batch.totalStartLiters - (lastLog?.litersRemaining ?? batch.totalStartLiters);
                                        return (
                                            <div key={batch.id} className={`bg-white border rounded-2xl overflow-hidden shadow-sm ${batch.status === 'active' ? 'border-blue-400' : 'border-blue-100'}`}>
                                                <div className={`px-5 py-4 flex items-center gap-4 flex-wrap ${batch.status === 'active' ? 'bg-blue-50' : 'bg-gray-50'}`}>
                                                    <div className="flex items-center gap-3">
                                                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-black text-lg ${batch.status === 'active' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500'}`}>
                                                            {batch.batchNo}
                                                        </div>
                                                        <div>
                                                            <p className="text-gray-800 font-bold">{batch.batchLabel}</p>
                                                            <p className="text-xs text-gray-400">{fmt(batch.deliveryDate)}</p>
                                                        </div>
                                                    </div>
                                                    <BatchBadge status={batch.status} />
                                                    <div className="flex flex-wrap gap-5 ml-2 text-sm">
                                                        <div><p className="text-xs text-gray-400">Old Remaining</p><p className="text-blue-500 font-bold">{fmtNum(batch.oldRemainingLiters)} L</p></div>
                                                        <div><p className="text-xs text-gray-400">+ New Delivery</p><p className="text-green-600 font-bold">+{fmtNum(batch.newDeliveryLiters)} L</p></div>
                                                        <div><p className="text-xs text-gray-400">= Opening</p><p className="text-blue-700 font-black">{fmtNum(batch.totalStartLiters)} L</p></div>
                                                        <div><p className="text-xs text-gray-400">Consumed</p><p className="text-red-500 font-bold">{fmtNum(Math.max(0, consumed))} L</p></div>
                                                        <div><p className="text-xs text-gray-400">Logs</p><p className="text-gray-600 font-bold">{batchLogs.length}</p></div>
                                                        {batch.grReference && <div><p className="text-xs text-gray-400">GR Ref</p><p className="text-gray-600 font-mono text-xs">{batch.grReference}</p></div>}
                                                    </div>
                                                    <button onClick={() => setShowBatchDetail(showBatchDetail?.id === batch.id ? null : batch)}
                                                        className="ml-auto text-xs text-blue-600 hover:text-blue-800 border border-blue-200 hover:border-blue-400 px-3 py-1.5 rounded-lg transition flex items-center gap-1 bg-white">
                                                        <Info className="w-3.5 h-3.5" />
                                                        {showBatchDetail?.id === batch.id ? 'Hide' : 'Details'}
                                                    </button>
                                                </div>
                                                {showBatchDetail?.id === batch.id && (
                                                    <div className="px-5 py-4 border-t border-blue-100 space-y-3">
                                                        <FuelGaugeBar current={Number(lastLog?.litersRemaining ?? batch.totalStartLiters)} max={Number(batch.totalStartLiters)} label="Remaining vs Opening" />
                                                        {batch.remarks && (
                                                            <div className="bg-blue-50 rounded-xl px-4 py-3 text-sm text-gray-600">
                                                                <span className="font-bold text-gray-500 mr-2">Remarks:</span>{batch.remarks}
                                                            </div>
                                                        )}
                                                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                                                            <div className="bg-blue-50 rounded-xl p-3">
                                                                <p className="text-gray-400 mb-1">Created By</p>
                                                                <p className="text-gray-700 font-semibold">{batch.createdByName || '—'}</p>
                                                            </div>
                                                            <div className="bg-blue-50 rounded-xl p-3">
                                                                <p className="text-gray-400 mb-1">Delivery Date</p>
                                                                <p className="text-gray-700 font-semibold">{fmt(batch.deliveryDate)}</p>
                                                            </div>
                                                            {batch.status === 'closed' && (
                                                                <>
                                                                    <div className="bg-blue-50 rounded-xl p-3">
                                                                        <p className="text-gray-400 mb-1">Closed At</p>
                                                                        <p className="text-gray-700 font-semibold">{fmt(batch.closedAt)}</p>
                                                                    </div>
                                                                    <div className="bg-blue-50 rounded-xl p-3">
                                                                        <p className="text-gray-400 mb-1">Closing Liters</p>
                                                                        <p className="text-blue-600 font-bold">{fmtNum(batch.closingLiters)} L</p>
                                                                    </div>
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}

                                {/* ✅ PAGINATION - Batch History */}
                                <div className="bg-white border border-blue-100 rounded-2xl overflow-hidden shadow-sm">
                                    <Pagination
                                        currentPage={batchPage}
                                        totalItems={fuelBatches.length}
                                        itemsPerPage={ITEMS_PER_PAGE}
                                        onPageChange={(p) => {
                                            setBatchPage(p);
                                            setShowBatchDetail(null);
                                        }}
                                    />
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>

            {/* RESET / NEW BATCH MODAL */}
            {showResetModal && (
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white border border-blue-200 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
                        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-5 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                                    <RotateCcw className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                    <h3 className="text-white font-black text-base">New GR Delivery / Reset Batch</h3>
                                    <p className="text-blue-200 text-xs mt-0.5">Closes current batch · Starts fresh</p>
                                </div>
                            </div>
                            <button onClick={() => setShowResetModal(false)} className="text-white/70 hover:text-white transition">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-6 space-y-4">
                            {activeBatch && (
                                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                                    <p className="text-xs text-blue-600 font-bold uppercase mb-3 flex items-center gap-1.5">
                                        <Info className="w-3.5 h-3.5" /> Current Batch Status
                                    </p>
                                    <div className="grid grid-cols-3 gap-3 text-sm">
                                        <div><p className="text-gray-400 text-xs">Batch</p><p className="text-blue-700 font-bold">{activeBatch.batchLabel}</p></div>
                                        <div><p className="text-gray-400 text-xs">Opening Was</p><p className="text-blue-600 font-bold">{fmtNum(activeBatch.totalStartLiters)} L</p></div>
                                        <div><p className="text-gray-400 text-xs">Latest Reading</p><p className="text-gray-800 font-bold">{fmtNum(latestLog?.litersRemaining ?? activeBatch.totalStartLiters)} L</p></div>
                                    </div>
                                </div>
                            )}

                            <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                                <p className="text-xs text-green-700 font-bold uppercase mb-2">Formula Preview</p>
                                <div className="flex flex-col gap-1.5 text-xs font-mono">
                                    {/* existing rows... */}
                                    {[...inventoryData]
                                        .sort((a, b) => b.id - a.id)
                                        .slice(1)
                                        .map((inv, i) => (
                                            <div key={i} className="flex items-center gap-2 text-gray-500">
                                                <span>├─ {inv.batchNo || `Batch ${i + 1}`}:</span>
                                                <span className="font-bold text-gray-700">{fmtNum(inv.quantity)} L</span>
                                                <span className="text-green-600 text-xs">← stays</span>
                                            </div>
                                        ))
                                    }
                                    {inventoryData.length > 0 && (() => {
                                        const latest = [...inventoryData].sort((a, b) => b.id - a.id)[0];
                                        return (
                                            <div className="flex items-center gap-2 text-red-500">
                                                <span>└─ {latest.batchNo || 'Latest'}:</span>
                                                <span className="font-bold">{fmtNum(latest.quantity)} L</span>
                                                <span className="text-gray-400">→ replaced by →</span>
                                                <span className="font-bold text-green-600">{resetForm.newDeliveryLiters || '?'} L</span>
                                            </div>
                                        );
                                    })()}
                                    <div className="border-t border-green-200 pt-2 mt-1 flex items-center gap-2">
                                        <span className="text-gray-500 font-bold">After reset total:</span>
                                        <span className="text-blue-700 font-black">
                                            {fmtNum(
                                                [...inventoryData]
                                                    .sort((a, b) => b.id - a.id)
                                                    .slice(1)
                                                    .reduce((s, i) => s + (Number(i.quantity) || 0), 0)
                                                + Number(resetForm.newDeliveryLiters || 0)
                                            )} L
                                        </span>
                                    </div>

                                    {/* ✅ DAGDAG: Remarks Preview */}
                                    <div className="border-t border-green-200 pt-2 mt-1">
                                        <p className="text-gray-500 font-bold mb-1">📝 Remarks Preview:</p>
                                        <p className="text-blue-700 font-semibold break-all leading-relaxed">
                                            {[
                                                activeBatch ? `Batch ${activeBatch.batchNo} Reset` : 'Batch Reset',
                                                `Remaining Fuel Liters ${activeBatch?.batchLabel || 'Current Batch'}: ${Number(latestLog?.litersRemaining ?? activeBatch?.totalStartLiters ?? 0)}L`,
                                                `New GR delivery: ${resetForm.newDeliveryLiters || '?'}L`,
                                                resetForm.grReference ? `GR Ref: ${resetForm.grReference}` : '',
                                                resetForm.notes || '',
                                            ].filter(Boolean).join(' | ')}
                                        </p>
                                    </div>
                                </div>
                            </div>


                            <div className="space-y-3">
                                <div>
                                    <label className="block text-xs font-bold text-blue-700 uppercase tracking-wide mb-1.5">New Delivery Liters *</label>
                                    <input type="number" value={resetForm.newDeliveryLiters}
                                        onChange={e => setResetForm(p => ({ ...p, newDeliveryLiters: e.target.value }))}
                                        placeholder="e.g. 20000"
                                        className="w-full bg-blue-50 border-2 border-blue-300 focus:border-blue-600 rounded-xl px-4 py-3 text-blue-800 font-black text-lg focus:outline-none transition placeholder-gray-300" />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">GR Reference No.</label>
                                        <select value={resetForm.grReference}
                                            onChange={e => {
                                                const selected = fuelGROptions.find(g => g.grNo === e.target.value);
                                                setResetForm(p => ({
                                                    ...p,
                                                    grReference: e.target.value,
                                                    // ✅ Auto-fill delivery liters from GR if user hasn't typed one yet
                                                    newDeliveryLiters: p.newDeliveryLiters || (selected?.liters?.toString() || ''),
                                                    deliveryDate: selected?.grDate
                                                        ? new Date(selected.grDate).toISOString().split('T')[0]
                                                        : p.deliveryDate,
                                                }));
                                            }}
                                            className="w-full bg-blue-50 border border-blue-200 rounded-xl px-3 py-2.5 text-sm text-gray-700 focus:outline-none focus:border-blue-500 transition font-mono">
                                            <option value="">— Select GR (GS1438) —</option>
                                            {fuelGROptions.map(gr => (
                                                <option key={gr.grNo} value={gr.grNo}>
                                                    {gr.grNo} · {fmtNum(gr.liters)} L · {fmt(gr.grDate)}
                                                </option>
                                            ))}
                                        </select>
                                        {fuelGROptions.length === 0 && (
                                            <p className="text-xs text-gray-400 mt-1">No GS1438 GRs found in system</p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">Delivery Date</label>
                                        <input type="date" value={resetForm.deliveryDate}
                                            onChange={e => setResetForm(p => ({ ...p, deliveryDate: e.target.value }))}
                                            className="w-full bg-blue-50 border border-blue-200 rounded-xl px-3 py-2.5 text-sm text-gray-700 focus:outline-none focus:border-blue-500 transition" />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">Notes</label>
                                    <input type="text" value={resetForm.notes}
                                        onChange={e => setResetForm(p => ({ ...p, notes: e.target.value }))}
                                        placeholder="e.g. Received from Petron, DO#12345"
                                        className="w-full bg-blue-50 border border-blue-200 rounded-xl px-3 py-2.5 text-sm text-gray-700 focus:outline-none focus:border-blue-500 transition" />
                                </div>
                            </div>

                            <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl p-3">
                                <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                                <p className="text-xs text-red-600">
                                    This will <strong>close the current batch</strong> and start a new one. Dipstick logs will be grouped under the new batch.
                                </p>
                            </div>
                        </div>

                        <div className="flex gap-3 px-6 py-4 border-t border-blue-100">
                            <button onClick={() => setShowResetModal(false)}
                                className="flex-1 py-3 border border-blue-200 rounded-xl text-gray-500 hover:text-blue-700 hover:border-blue-400 font-semibold text-sm transition">
                                Cancel
                            </button>
                            <button onClick={handleReset} disabled={submitting || !resetForm.newDeliveryLiters}
                                className="flex-1 py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-30 disabled:cursor-not-allowed text-white font-black rounded-xl text-sm transition shadow-lg flex items-center justify-center gap-2">
                                {submitting
                                    ? <><Loader2 className="w-4 h-4 animate-spin" /> Processing...</>
                                    : <><RotateCcw className="w-4 h-4" /> Start New Batch</>}
                            </button>
                        </div>
                    </div>
                </div>
            )
            }
        </div >
    );
}